import { FC, useState, useEffect, memo, useMemo } from 'react'
import cx from 'classnames'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { useTranslation } from 'next-i18next'
import { useTracking } from '../../services/context/TrackingContext'
import { useCalculatorTracking } from '../../hooks/useAnalyticsIdentification'

import { formatStandardListingProperties } from '../ListingsGrid'

import useAcreCalculator, {
  CalculatorPageContent,
  Product,
} from '../../hooks/useAcreCalculator'

import { Listing } from '../../services/getListings'
import Link from 'next/link'
import useAuth from '../../hooks/useAuth'
import { useAppState } from '../../services/context/AppContext'
import {
  PaymentSelection,
  selectPayment,
} from '../../services/flags/selectPayment'
import PaymentExtrasSwitch from '../payment/PaymentExtrasSwitch'
import MonthlyPaymentBreakdown from '../payment/MonthlyPaymentBreakdown'
import PaymentBreakdownCard from '../payment/PaymentBreakdownCard'

import { numberToCurrency } from 'utils'
import { CalculatorIcon } from 'icons'
import { TertiaryButtonLink } from 'ui'

const ProductOption = memo(function ProductOption({
  product,
  isActive,
  label,
  monthlyPayment,
  description,
}: {
  product: Product
  isActive: boolean
  label: string
  /** Null when `selectPayment` declined to quote a price for this listing. */
  monthlyPayment: number | null
  description: string
}) {
  const { t } = useTranslation('homes')
  return (
    <RadioGroup.Item
      key={product}
      value={product}
      className={cx('relative rounded border p-24', {
        'border-grey-3 bg-white hover:ring-2': !isActive,
        'hover:border-teal-3 hover:ring-teal-3':
          !isActive && product === 'saver',
        'border-teal-3 bg-teal-1 ring-2 ring-teal-3':
          isActive && product === 'saver',
        'hover:border-tan-3 hover:ring-tan-3': !isActive && product === 'boost',
        'border-tan-3 bg-tan-1 ring-2 ring-tan-3':
          isActive && product === 'boost',
      })}
    >
      <p className="text-14 font-semibold">{label}</p>
      <p className="mt-16 hd-lg" data-testid={`${product}-monthly-payment`}>
        {monthlyPayment === null
          ? t('contactUsForPricing')
          : `${numberToCurrency(monthlyPayment)} /${t('monthAbbreviation')}`}
      </p>
      <p className="mt-4 util-caption" data-testid={`${product}-wealth-built`}>
        {description}
      </p>
    </RadioGroup.Item>
  )
})

const ExplainerRow = memo(function ExplainerRow({
  label,
  description,
  value,
  product,
}: {
  label: string
  description: string
  value: number
  product?: Product
}) {
  return (
    <li className="flex">
      <div className="grow">
        <h4 className="util-label">{label}</h4>
        <p className="mt-4 util-caption">{description}</p>
      </div>
      <p
        className={cx('ml-32 mt-8 min-w-[123px] shrink-0 hd-xl', {
          'text-teal-3': product === 'saver',
          'text-tan-3': product === 'boost',
        })}
      >
        {numberToCurrency(value)}
      </p>
    </li>
  )
})

export const ProductsForm: FC<{
  listingId: number
  handleSeeTheNumbersButtonClick?: () => void
}> = ({ children, listingId, handleSeeTheNumbersButtonClick }) => {
  const { t } = useTranslation('homes')

  return (
    <form>
      <div className="grid grid-cols-2 gap-16">
        <h3 className="util-label" id="acre-strategy-label">
          {t('listingProducts.title')}
        </h3>
        <p className="flex items-center justify-end space-x-8">
          <CalculatorIcon className="stroke-teal-3 rect-24" />
          <Link href={`/calculator${listingId ? `?lid=${listingId}` : ''}`}>
            <TertiaryButtonLink
              className="inline-flex cursor-pointer items-center"
              onClick={handleSeeTheNumbersButtonClick}
            >
              See the numbers
            </TertiaryButtonLink>
          </Link>
        </p>
      </div>
      {children}
    </form>
  )
}

const useListingCalculator = (
  listing: Listing,
  calculator: CalculatorPageContent
) =>
  useAcreCalculator({
    interestRate: calculator.interestRateAcre,
    interestRateAcre: listing.interestRate ?? calculator.interestRateAcre,
    expectedClose: listing.estimatedSalePrice,
    assumptions: {
      ...calculator,
      propertyAnnualTaxAmount: listing.taxes.annualAmount,
    },
    market: listing.market,
  })

/**
 * The listing's "Monthly payment breakdown": the v3 block's product base, tax,
 * insurance and HOA, against the mortgage base from the same calculator inputs
 * the product cards use.
 */
export const ListingPaymentBreakdown = ({
  listing,
  calculator,
  product,
  payment,
}: {
  listing: Listing
  calculator: CalculatorPageContent
  product: Product
  payment: Extract<PaymentSelection, { kind: 'v3' }>
}) => {
  const { mortgageFinancingBase } = useListingCalculator(listing, calculator)

  return (
    <MonthlyPaymentBreakdown
      product={product}
      productBase={payment.products[product].base}
      mortgageBase={mortgageFinancingBase}
      extras={payment}
      flush
    />
  )
}

/**
 * Make-offer's breakdown card for the selected product. Its total is the
 * product's `total`, the figure `onSelect` records.
 */
export const OfferPaymentBreakdown = ({
  listing,
  calculator,
  product,
  payment,
}: {
  listing: Listing
  calculator: CalculatorPageContent
  product: Product
  payment: Extract<PaymentSelection, { kind: 'v3' }>
}) => {
  const { mortgageFinancingBase } = useListingCalculator(listing, calculator)
  const { base, total } = payment.products[product]

  return (
    <PaymentBreakdownCard
      product={product}
      productBase={base}
      total={total}
      mortgageBase={mortgageFinancingBase}
      extras={payment}
    />
  )
}

const AcreProducts = memo(function AcreProducts({
  listing,
  calculator,
  initialProduct,
  trackEventName,
  onSelect,
  onProductChange,
  onSeeBreakdown,
}: {
  listing: Listing
  calculator: CalculatorPageContent
  initialProduct: Product
  trackEventName?: string
  onSelect?: (
    product: Product,
    valueShare: number,
    /** Null when `selectPayment` declined to quote — never a stand-in figure. */
    monthlyPayment: number | null
  ) => void
  onProductChange?: (product: Product) => void
  /** Shows the "see breakdown" link in the taxes, insurance and HOA row. */
  onSeeBreakdown?: () => void
}) {
  const { t } = useTranslation('homes')
  const { track } = useTracking()
  const { user } = useAuth()
  const calculatorTracking = useCalculatorTracking(user?.email || '')
  const [selectedProduct, setSelectedProduct] =
    useState<Product>(initialProduct)
  const [includeExtras, setIncludeExtras] = useState(false)
  const {
    state: { globalFlags },
  } = useAppState()

  const {
    valueShare,
    saverMonthlyPayment,
    saverNetValue,
    boostMonthlyPayment,
    boostNetValue,
    mortgageNetValue,
    monthlyTotalMortgage,
  } = useListingCalculator(listing, calculator)

  /**
   * The committed price. `valueShare` and the net-value figures below stay
   * hook-derived: reconciling them with v3 is PDE follow-up work, not this
   * change. `selectedPaymentFor` is the all-in figure that `onSelect` records
   * and analytics receive; what the browse surfaces display is resolved
   * separately (see the display/commit split below), so a base headline and an
   * all-in recorded value are intentional, not a mismatch to reconcile.
   */
  const payment = selectPayment(listing, globalFlags)
  const selectedPaymentFor = (
    product: Product,
    hookPayment: number
  ): number | null => {
    switch (payment.kind) {
      case 'v3':
        return payment.products[product].total
      case 'unavailable':
        return null
      default:
        return hookPayment
    }
  }

  // Base-forward preview (pending Irene sign-off): the detail card shows the
  // financing base while make-offer shows and records the all-in. The two
  // AcreProducts call sites split on `onSelect` -- only make-offer passes it --
  // so its absence marks the browse/detail surface. This is a deliberate
  // shortcut to be replaced by an explicit `headline` prop once the direction
  // is locked. The committed value (onSelect, tracking) stays the all-in total.
  // The taxes, insurance and HOA switch only adds to that base display, so it
  // never changes what make-offer records.
  const displayBase = !onSelect
  const showExtrasRow = payment.kind === 'v3' && displayBase
  // Make-offer's tiles already show the all-in figure, so its row is a static
  // statement rather than a switch.
  const showIncludedRow = payment.kind === 'v3' && !displayBase
  const displayPaymentFor = (
    product: Product,
    hookPayment: number
  ): number | null => {
    if (payment.kind !== 'v3' || !displayBase) {
      return selectedPaymentFor(product, hookPayment)
    }
    const { base, total } = payment.products[product]
    return includeExtras ? total : base
  }

  const saverPayment = selectedPaymentFor('saver', saverMonthlyPayment)
  const boostPayment = selectedPaymentFor('boost', boostMonthlyPayment)
  const saverDisplay = displayPaymentFor('saver', saverMonthlyPayment)
  const boostDisplay = displayPaymentFor('boost', boostMonthlyPayment)
  const selectedPayment =
    selectedProduct === 'saver' ? saverPayment : boostPayment

  const { saverVsMortgageNetValue, boostVsMortgageNetValue, saverDescription } = useMemo(() => {
    const saverDiff = saverNetValue - mortgageNetValue
    const boostDiff = boostNetValue - mortgageNetValue

    const description = saverDiff > 0
      ? `${t('listingProducts.saverButtonDescription')} ${t(
          'listingProducts.sharedButtonDescription',
          {
            wealth: numberToCurrency(saverDiff),
          }
        )}`
      : t('listingProducts.saverNetNegativeButtonDescription', {
          monthlyPaymentSavings: numberToCurrency(
            monthlyTotalMortgage - saverMonthlyPayment
          ),
        })

    return {
      saverVsMortgageNetValue: saverDiff,
      boostVsMortgageNetValue: boostDiff,
      saverDescription: description
    }
  }, [saverNetValue, mortgageNetValue, boostNetValue, t, monthlyTotalMortgage, saverMonthlyPayment])

  useEffect(() => {
    if (onSelect) {
      onSelect(selectedProduct, valueShare, selectedPayment)
    }

    // Track product comparison in analytics
    if (calculatorTracking && user) {
      calculatorTracking.trackProductComparison({
        saverPayment,
        boostPayment,
        saverValue: saverNetValue,
        boostValue: boostNetValue,
        selectedProduct,
        propertyId: listing.id.toString(),
      })
    }
  }, [onSelect, selectedProduct, valueShare, boostPayment, saverPayment, selectedPayment, calculatorTracking, saverNetValue, boostNetValue, listing.id, user])

  return (
    <>
      <RadioGroup.Root
        value={selectedProduct}
        onValueChange={(value) => {
          const newProduct = value as Product
          setSelectedProduct(newProduct)
          onProductChange?.(newProduct)

          if (trackEventName) {
            track({
              event: trackEventName,
              properties: {
                ...formatStandardListingProperties(listing),
                listing_target_model: value,
                listing_saver_wealth_built: saverNetValue,
                listing_boost_wealth_built: boostNetValue,
                listing_saver_wealth_difference: saverVsMortgageNetValue,
                listing_boost_wealth_difference: boostVsMortgageNetValue,
              },
            })
          }

          // Track calculator usage in analytics
          if (calculatorTracking && user) {
            calculatorTracking.trackCalculatorUsage({
              purchasePrice: listing.estimatedSalePrice,
              interestRate: calculator.interestRateAcre,
              productType: newProduct,
              monthlyPayment:
                newProduct === 'saver' ? saverPayment : boostPayment,
              valueShare,
              totalValue:
                newProduct === 'saver' ? saverNetValue : boostNetValue,
              propertyId: listing.id.toString(),
            })
          }
        }}
        className="mt-20 grid grid-cols-1 gap-16 xs:grid-cols-2"
        aria-labelledby="acre-strategy-label"
      >
        <ProductOption
          product="saver"
          label={t('acreSaver')}
          isActive={selectedProduct === 'saver'}
          monthlyPayment={saverDisplay}
          description={saverDescription}
        />
        <ProductOption
          product="boost"
          label={t('acreBoost')}
          isActive={selectedProduct === 'boost'}
          monthlyPayment={boostDisplay}
          description={`${t('listingProducts.boostButtonDescription')} ${t(
            'listingProducts.sharedButtonDescription',
            {
              wealth: numberToCurrency(boostVsMortgageNetValue),
            }
          )}`}
        />
      </RadioGroup.Root>
      {showExtrasRow && (
        // Below lg the link always wraps to its own line; from lg all three
        // sit on one line, right-aligned under the cards.
        <div className="mt-12 flex flex-wrap items-center gap-x-12 gap-y-6 lg:justify-end">
          <span
            id="payment-extras-label"
            className="text-14 leading-5 text-[#5C5C5C]"
          >
            {t('paymentExtras.label')}
          </span>
          <div className="ml-auto flex lg:ml-0">
            <PaymentExtrasSwitch
              checked={includeExtras}
              onCheckedChange={setIncludeExtras}
              labelledBy="payment-extras-label"
            />
          </div>
          {onSeeBreakdown && (
            <div className="flex basis-full lg:basis-auto">
              <button
                type="button"
                onClick={onSeeBreakdown}
                className="text-14 leading-5 text-[#0B8381] underline"
              >
                {t('paymentExtras.seeBreakdown')}
              </button>
            </div>
          )}
        </div>
      )}
      {showIncludedRow && (
        <p className="mt-12 flex items-center justify-between gap-12 text-14 leading-5 text-grey-7 lg:justify-end">
          <span>{t('paymentExtras.label')}</span>
          <span className="inline-flex h-24 items-center rounded-full bg-teal-4 px-12 text-12 font-semibold leading-none text-white">
            {t('paymentExtras.included')}
          </span>
        </p>
      )}
      <ul className="mt-24 space-y-24">
        <ExplainerRow
          label={t('valueShare')}
          description={t('listingProducts.valueShareDescription')}
          value={valueShare}
        />
        <ExplainerRow
          label={t('listingProducts.wealthTitle')}
          description={
            selectedProduct === 'saver'
              ? t('listingProducts.wealthSaverDescription')
              : t('listingProducts.wealthBoostDescription')
          }
          value={selectedProduct === 'saver' ? saverNetValue : boostNetValue}
          product={selectedProduct}
        />
      </ul>
    </>
  )
})

export default AcreProducts
